import React, { useState } from 'react';
import { DocumentUploader } from './DocumentUploader';
import { PlusIcon, FileTextIcon, BriefcaseIcon, CalendarIcon, DollarSignIcon } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
interface IncomeSectionProps {
  applicationId: string;
}
export function IncomeSection({
  applicationId
}: IncomeSectionProps) {
  const {
    addToast
  } = useToast();
  const [activeTab, setActiveTab] = useState('employment');
  // Mock income data
  const incomeData = {
    employment: [{
      id: '1',
      employer: 'Acme Corporation',
      title: 'Senior Software Engineer',
      startDate: '2021-06-15',
      employmentType: 'Full-time',
      income: {
        amount: 115000,
        frequency: 'annual',
        ytdEarnings: 28750,
        lastYearEarnings: 110000
      },
      verified: true
    }],
    selfEmployment: [],
    otherIncome: [{
      id: '1',
      source: 'Rental Income',
      amount: 1800,
      frequency: 'monthly',
      annualAmount: 21600,
      verified: false
    }]
  };
  const handleDocumentUpload = (files: File[]) => {
    // In a real app, this would upload the files to a server
    addToast(`${files.length} document(s) uploaded successfully`, 'success');
  };
  const handleAddIncome = () => {
    // In a real app, this would open a modal to add income
    addToast('Income added successfully', 'success');
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  return <div className="bg-white rounded-md shadow-sm border border-neutral-200 overflow-hidden" data-id="element-336">
      <div className="flex items-center justify-between p-4 border-b border-neutral-200" data-id="element-337">
        <h2 className="text-xl font-medium text-neutral-900" data-id="element-338">Income</h2>
        <button onClick={handleAddIncome} className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500" data-id="element-339">
          <PlusIcon className="mr-1 h-4 w-4" data-id="element-340" />
          Add Income
        </button>
      </div>
      <div className="border-b border-neutral-200" data-id="element-341">
        <nav className="flex -mb-px" data-id="element-342">
          <button className={`px-4 py-3 border-b-2 text-sm font-medium ${activeTab === 'employment' ? 'border-primary-500 text-primary-600' : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'}`} onClick={() => setActiveTab('employment')} data-id="element-343">
            Employment
          </button>
          <button className={`px-4 py-3 border-b-2 text-sm font-medium ${activeTab === 'selfEmployment' ? 'border-primary-500 text-primary-600' : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'}`} onClick={() => setActiveTab('selfEmployment')} data-id="element-344">
            Self-Employment
          </button>
          <button className={`px-4 py-3 border-b-2 text-sm font-medium ${activeTab === 'otherIncome' ? 'border-primary-500 text-primary-600' : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'}`} onClick={() => setActiveTab('otherIncome')} data-id="element-345">
            Other Income
          </button>
        </nav>
      </div>
      <div className="p-4" data-id="element-346">
        {activeTab === 'employment' && <div data-id="element-347">
            {incomeData.employment.length > 0 ? <div className="space-y-6" data-id="element-348">
                {incomeData.employment.map(job => <div key={job.id} className="bg-neutral-50 rounded-lg p-4" data-id="element-349">
                    <div className="flex items-start justify-between" data-id="element-350">
                      <div data-id="element-351">
                        <h3 className="text-lg font-medium text-neutral-900" data-id="element-352">
                          {job.employer}
                        </h3>
                        <p className="text-sm text-neutral-500" data-id="element-353">{job.title}</p>
                      </div>
                      {job.verified && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-800" data-id="element-354">
                          Verified
                        </span>}
                    </div>
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-355">
                      <div className="flex items-center" data-id="element-356">
                        <BriefcaseIcon className="h-5 w-5 text-neutral-400 mr-2" data-id="element-357" />
                        <div data-id="element-358">
                          <p className="text-xs text-neutral-500" data-id="element-359">
                            Employment Type
                          </p>
                          <p className="text-sm font-medium" data-id="element-360">
                            {job.employmentType}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center" data-id="element-361">
                        <CalendarIcon className="h-5 w-5 text-neutral-400 mr-2" data-id="element-362" />
                        <div data-id="element-363">
                          <p className="text-xs text-neutral-500" data-id="element-364">Start Date</p>
                          <p className="text-sm font-medium" data-id="element-365">
                            {formatDate(job.startDate)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center" data-id="element-366">
                        <DollarSignIcon className="h-5 w-5 text-neutral-400 mr-2" data-id="element-367" />
                        <div data-id="element-368">
                          <p className="text-xs text-neutral-500" data-id="element-369">
                            Annual Income
                          </p>
                          <p className="text-sm font-medium" data-id="element-370">
                            {formatCurrency(job.income.amount)}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-neutral-200" data-id="element-371">
                      <h4 className="text-sm font-medium text-neutral-700 mb-2" data-id="element-372">
                        Income Details
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-373">
                        <div data-id="element-374">
                          <p className="text-xs text-neutral-500" data-id="element-375">
                            YTD Earnings
                          </p>
                          <p className="text-sm font-medium" data-id="element-376">
                            {formatCurrency(job.income.ytdEarnings)}
                          </p>
                        </div>
                        <div data-id="element-377">
                          <p className="text-xs text-neutral-500" data-id="element-378">
                            Previous Year Earnings
                          </p>
                          <p className="text-sm font-medium" data-id="element-379">
                            {formatCurrency(job.income.lastYearEarnings)}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 flex justify-end" data-id="element-380">
                      <button className="text-sm text-primary-600 hover:text-primary-700 font-medium" data-id="element-381">
                        Edit Details
                      </button>
                    </div>
                  </div>)}
                <div className="mt-6" data-id="element-382">
                  <h3 className="text-lg font-medium text-neutral-900 mb-3" data-id="element-383">
                    Upload Income Documents
                  </h3>
                  <DocumentUploader onUpload={handleDocumentUpload} data-id="element-384" />
                  <div className="mt-4" data-id="element-385">
                    <h4 className="text-sm font-medium text-neutral-700 mb-2" data-id="element-386">
                      Required Documents
                    </h4>
                    <ul className="space-y-2" data-id="element-387">
                      <li className="flex items-start" data-id="element-388">
                        <FileTextIcon className="h-5 w-5 text-neutral-400 mr-2" data-id="element-389" />
                        <span className="text-sm text-neutral-700" data-id="element-390">
                          Recent pay stubs (last 30 days)
                        </span>
                      </li>
                      <li className="flex items-start" data-id="element-391">
                        <FileTextIcon className="h-5 w-5 text-neutral-400 mr-2" data-id="element-392" />
                        <span className="text-sm text-neutral-700" data-id="element-393">
                          W-2 statements (last 2 years)
                        </span>
                      </li>
                      <li className="flex items-start" data-id="element-394">
                        <FileTextIcon className="h-5 w-5 text-neutral-400 mr-2" data-id="element-395" />
                        <span className="text-sm text-neutral-700" data-id="element-396">
                          Employment verification letter
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div> : <div className="text-center py-8" data-id="element-397">
                <BriefcaseIcon className="mx-auto h-12 w-12 text-neutral-400" data-id="element-398" />
                <h3 className="mt-2 text-sm font-medium text-neutral-900" data-id="element-399">
                  No employment income added
                </h3>
                <p className="mt-1 text-sm text-neutral-500" data-id="element-400">
                  Add employment details to continue
                </p>
                <div className="mt-6" data-id="element-401">
                  <button onClick={handleAddIncome} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500" data-id="element-402">
                    <PlusIcon className="mr-1 h-4 w-4" data-id="element-403" />
                    Add Employment
                  </button>
                </div>
              </div>}
          </div>}
        {activeTab === 'selfEmployment' && <div className="text-center py-8" data-id="element-404">
            <BriefcaseIcon className="mx-auto h-12 w-12 text-neutral-400" data-id="element-405" />
            <h3 className="mt-2 text-sm font-medium text-neutral-900" data-id="element-406">
              No self-employment income added
            </h3>
            <p className="mt-1 text-sm text-neutral-500" data-id="element-407">
              Add self-employment details to continue
            </p>
            <div className="mt-6" data-id="element-408">
              <button onClick={handleAddIncome} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500" data-id="element-409">
                <PlusIcon className="mr-1 h-4 w-4" data-id="element-410" />
                Add Self-Employment
              </button>
            </div>
          </div>}
        {activeTab === 'otherIncome' && <div data-id="element-411">
            {incomeData.otherIncome.length > 0 ? <div className="space-y-4" data-id="element-412">
                {incomeData.otherIncome.map(income => <div key={income.id} className="bg-neutral-50 rounded-lg p-4" data-id="element-413">
                    <div className="flex items-start justify-between" data-id="element-414">
                      <div data-id="element-415">
                        <h3 className="text-lg font-medium text-neutral-900" data-id="element-416">
                          {income.source}
                        </h3>
                        <p className="text-sm text-neutral-500" data-id="element-417">
                          {formatCurrency(income.amount)} per month (
                          {formatCurrency(income.annualAmount)} annually)
                        </p>
                      </div>
                      {income.verified ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-800" data-id="element-418">
                          Verified
                        </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-warning-100 text-warning-800" data-id="element-419">
                          Pending Verification
                        </span>}
                    </div>
                    <div className="mt-4 flex justify-end" data-id="element-420">
                      <button className="text-sm text-primary-600 hover:text-primary-700 font-medium" data-id="element-421">
                        Edit Details
                      </button>
                    </div>
                  </div>)}
                <div className="mt-6" data-id="element-422">
                  <h3 className="text-lg font-medium text-neutral-900 mb-3" data-id="element-423">
                    Upload Supporting Documents
                  </h3>
                  <DocumentUploader onUpload={handleDocumentUpload} data-id="element-424" />
                </div>
              </div> : <div className="text-center py-8" data-id="element-425">
                <DollarSignIcon className="mx-auto h-12 w-12 text-neutral-400" data-id="element-426" />
                <h3 className="mt-2 text-sm font-medium text-neutral-900" data-id="element-427">
                  No other income added
                </h3>
                <p className="mt-1 text-sm text-neutral-500" data-id="element-428">
                  Add other income sources if applicable
                </p>
                <div className="mt-6" data-id="element-429">
                  <button onClick={handleAddIncome} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500" data-id="element-430">
                    <PlusIcon className="mr-1 h-4 w-4" data-id="element-431" />
                    Add Other Income
                  </button>
                </div>
              </div>}
          </div>}
      </div>
    </div>;
}